/**
 * The step ledger: what each step of an exploration drew, and the running figure
 * by any point of the replay.
 *
 * The per-model ledger (`session.spend`) is the record a user checks against a
 * provider's dashboard — pooled per model, which is exactly why it cannot say
 * WHICH step drew what. This module reads the per-op rows (`Op.spend`) instead
 * and lays them along the replay, so the step rail, its preview card and the
 * scrubbed meter all read one arithmetic. The rule is scope, not preference:
 *
 *   - "what has this exploration drawn" → `estimateSpendUsd(session.spend)`,
 *     everywhere a LIVE figure is shown. It includes the Ask channel and every
 *     pass recorded before per-op rows existed, neither of which sits on a step.
 *   - "what had it drawn by step i"     → `stepLedger(session).spentAt(i)`,
 *     everywhere a SCRUBBED figure is shown, and for the shape of the rail.
 *
 * The two agree wherever both can speak; the first also carries draws the second
 * can place on no step, and `unrecorded` / `unpriced` surface those gaps rather
 * than letting them read as free.
 *
 * Steps are `session.order` — insertion order, which is already the replay order
 * the playhead scrubs. (The 13a design sheet orders by depth with insertion as
 * the tie-break; here the rail seeks the same playhead the transport owns, and
 * two orders would make tick i and scrub position i disagree about which step
 * they name.)
 *
 * A pass that emitted several nodes has its draw split EVENLY across them. That
 * is arithmetic, not attribution — one call deliberated and chose all of them,
 * and the trace panel already refuses to split the deliberation — but the split
 * keeps every cumulative figure exact at pass boundaries, which is what the rail
 * actually promises. `splitAcross` is carried so a surface can say "one pass,
 * three moves" instead of implying the step was priced alone.
 *
 * Every figure here leans high (`estimateUsd`, at list), the same direction as
 * the rest of the app. Nothing below may call any of it a bill.
 */

import type { NodeOrigin, OpKind, Session, Spend } from './types'
import { estimateUsd, priceOf } from './models'

/**
 * Why a step shows the figure it shows. Four states, because zero has three
 * different meanings and collapsing them would let one lie for the others:
 * `free` is a pass that called no model (the seed, a demo node, your own
 * claim); `unrecorded` is model-authored work whose per-op row no longer
 * exists, so its draw is in the per-model ledger and on no step; `unpriced` is
 * a pass on a model this app has no list rate for.
 */
export type StepCostKind = 'charged' | 'free' | 'unrecorded' | 'unpriced'

export interface Step {
  nodeId: string
  /** Estimated dollars this step drew. 0 unless `kind === 'charged'`. */
  usd: number
  kind: StepCostKind
  /** How many steps the producing pass was split across. 1 = priced alone. */
  splitAcross: number
  /** The node's own origin — what verb landed it. */
  origin: NodeOrigin
  /** Logical angles sharing the producing pass, when it was a portfolio. */
  angles?: number
}

/**
 * A pass that spent and landed no node: a source check that attached a record, a
 * panel whose lenses convicted nothing, a breakthrough scan, a link search.
 * Anchored to the last step already on the board when it finished, so scrubbing
 * past that step carries the charge — spend is a property of the point in the
 * run you are looking at, and these passes happened at a point.
 */
export interface SideCharge {
  afterStep: number
  usd: number
  kind: OpKind
}

export interface StepLedger {
  steps: Step[]
  sides: SideCharge[]
  /** Estimated dollars drawn by steps 0…i plus side charges anchored at ≤ i.
   *  Monotonic. `spentAt(-1)` is 0; past the end it holds the last figure. */
  spentAt: (i: number) => number
  /** The largest single-step draw — the scale the rail's tick lengths encode. */
  maxStep: number
  /** Everything this ledger can place on a step: `spentAt(last)`. */
  attributed: number
  /** Passes on a model with no list rate. Their draw is in no figure here. */
  unpriced: number
  /** Model passes with no per-op row, plus one conservative stand-in for each
   *  model-stamped step whose producing op was truncated. Drawn, but placeable
   *  on no step. */
  unrecorded: number
}

/** The spending cap as typed in the composer, or null for none. Empty string,
 *  zero and garbage all mean "no cap" — the same reading `startRun` takes. */
export function parseCap(text: string): number | null {
  const n = Number.parseFloat(text)
  return Number.isFinite(n) && n > 0 ? n : null
}

/** The first step whose cumulative figure crosses the cap, or −1. Crossing
 *  blocks nothing — the rail draws a hairline there and the meter turns, and
 *  that is the whole enforcement outside a run. */
export function capIndex(ledger: StepLedger, cap: number | null): number {
  if (cap === null) return -1
  for (let i = 0; i < ledger.steps.length; i++) {
    if (ledger.spentAt(i) > cap) return i
  }
  return -1
}

export function stepLedger(session: Session): StepLedger {
  // First producer wins. No op produces a node twice; the guard is for a
  // hand-edited document, where charging one node from two passes would count
  // the same money twice.
  const producing = new Map<string, Session['ops'][number]>()
  for (const op of session.ops) {
    for (const id of op.produced) if (!producing.has(id)) producing.set(id, op)
  }
  // How many steps each op actually OWNS — its produced nodes minus any a prior
  // op already claimed, minus any pruned. The divisor for the even split, and
  // the guarantee behind it: every priced row lands exactly once, split across
  // the steps it owns, or whole as a side charge when it owns none. Dividing by
  // `produced.length` instead would leak money on any document where the two
  // disagree — the sum of the shares is the promise, so the divisor must be the
  // number of shares that actually exist.
  const owned = new Map<string, number>()
  for (const id of session.order) {
    const op = producing.get(id)
    if (op) owned.set(op.id, (owned.get(op.id) ?? 0) + 1)
  }

  let unpriced = 0
  let unrecorded = 0

  const steps: Step[] = session.order.map((id) => {
    const node = session.nodes[id]
    const origin = node?.origin ?? 'manual'
    const op = producing.get(id)
    const angles = op?.portfolio?.parts.length
    const packet = angles ? { angles } : {}
    // Ops are a bounded ledger but model provenance lives on the node forever.
    // Once its producing op ages out we cannot recover which siblings shared
    // the pass, so count the node as one conservative unrecorded stand-in. It
    // must not become `free`: the surviving stamp proves a model was called.
    if (!op && node?.model) {
      unrecorded++
      return { nodeId: id, usd: 0, kind: 'unrecorded', splitAcross: 1, origin }
    }
    // No op and no model stamp, or an old op that stamped no model: nothing is
    // known to have been called. The demo and the user's own claims land here.
    if (!op?.model) return { nodeId: id, usd: 0, kind: 'free', splitAcross: 1, origin, ...packet }
    const across = owned.get(op.id) ?? 1
    if (!op.spend) {
      return {
        nodeId: id,
        usd: 0,
        kind: 'unrecorded',
        splitAcross: across,
        origin,
        ...packet,
      }
    }
    const p = priceOf(op.spend.model.id)
    if (!p) return { nodeId: id, usd: 0, kind: 'unpriced', splitAcross: across, origin, ...packet }
    const share = estimateUsd(op.spend, p) / Math.max(1, across)
    return { nodeId: id, usd: share, kind: 'charged', splitAcross: across, origin, ...packet }
  })

  // Ops counted once each, not once per node they produced.
  for (const op of session.ops) {
    if (!op.model) continue
    if (!op.spend) {
      unrecorded++
      continue
    }
    if (!priceOf(op.spend.model.id)) unpriced++
  }

  // Where a side charge lands: the last step already born when the pass ended.
  // Births are non-decreasing along `order` by construction, so a linear walk
  // finds the last index at or before the time — few side passes, short walk.
  const births = session.order.map((id) => session.nodes[id]?.createdAt ?? 0)
  const anchor = (t: number): number => {
    let at = 0
    for (let j = 0; j < births.length; j++) if ((births[j] ?? 0) <= t) at = j
    return at
  }

  const sides: SideCharge[] = []
  for (const op of session.ops) {
    // A pass that owns no step: it landed no node, or everything it landed has
    // been pruned or was claimed by an earlier record. Its draw still happened
    // at a point in the run, so it rides as a side charge rather than vanishing.
    if (!op.spend || (owned.get(op.id) ?? 0) > 0) continue
    const p = priceOf(op.spend.model.id)
    if (!p) continue // already counted in `unpriced` above
    sides.push({
      afterStep: anchor(op.finishedAt ?? op.startedAt),
      usd: estimateUsd(op.spend, p),
      kind: op.kind,
    })
  }

  const sideAt = new Map<number, number>()
  for (const s of sides) sideAt.set(s.afterStep, (sideAt.get(s.afterStep) ?? 0) + s.usd)

  const cum: number[] = []
  let acc = 0
  steps.forEach((st, i) => {
    acc += st.usd + (sideAt.get(i) ?? 0)
    cum.push(acc)
  })

  const spentAt = (i: number): number =>
    i < 0 || cum.length === 0 ? 0 : (cum[Math.min(i, cum.length - 1)] ?? 0)

  return {
    steps,
    sides,
    spentAt,
    maxStep: steps.reduce((m, s) => Math.max(m, s.usd), 0),
    attributed: acc,
    unpriced,
    unrecorded,
  }
}

/**
 * What the provider said a receipt's input was made of.
 *
 * COUNTS, and deliberately not money. `estimateUsd` prices these same categories
 * itself since D311 — a read at the model's read rate, a write with its premium —
 * so pricing them again here would be a second dollar figure that could come to
 * disagree with the first, on the same screen, about the same run. What a reader
 * gets instead is the fact the estimate is built on, stated plainly enough to be
 * checked: how much of the input was served from cache.
 *
 * Null when the row carries no breakdown, which is a real state and not an empty
 * one: a receipt recorded before the meter existed cannot answer the question,
 * and a zero would answer it wrongly. Also null when a row's input is entirely
 * unclassified — an aggregate merged in from an older build has nothing to say
 * about caching either, and `cached: 0` would read as "the cache never fired"
 * rather than "nobody measured".
 */
export interface CacheFacts {
  /** Input served from cache, billed at the model's read rate. */
  cached: number
  /** Input written to cache, billed above the base rate. */
  written: number
  /** Input billed at the base rate. */
  uncached: number
  /** The denominator `cachedPct` is taken over: input whose category this row
   *  knows. Always positive, or there would be no facts to report. */
  measured: number
  /**
   * Input this row cannot categorise — recorded before the meter, or reported
   * without a breakdown. Excluded from `cachedPct` on purpose: diluting the
   * share with tokens nobody measured would understate a cache that is working.
   *
   * Nonzero is the ORDINARY case on the first pass after an upgrade, not an edge
   * one — a stored ledger row merges its whole aggregate in as unclassified. So
   * any surface printing `cachedPct` must say which denominator it used whenever
   * this is above zero, or it claims a whole-row measurement it does not have.
   */
  unclassified: number
  /** `cached` as a share of `measured`, 0–100, rounded. */
  cachedPct: number
}

export function cacheFacts(row: Spend): CacheFacts | null {
  const d = row.detail
  if (!d) return null
  const written = d.cacheWrite5m + d.cacheWrite30m + d.cacheWrite1h
  const measured = d.inputUncached + written + d.cacheRead
  if (measured <= 0) return null
  return {
    cached: d.cacheRead,
    written,
    uncached: d.inputUncached,
    measured,
    unclassified: d.inputUnclassified,
    cachedPct: Math.round((d.cacheRead / measured) * 100),
  }
}
